/**
 * PmJobSheet — one job's day guide (Sep 11, 2026).
 *
 * Header (address → maps, RM, colors, squares, drop, crew instructions),
 * the Do/Don't card, then the guide grouped by section: every item is a big
 * tap target — a checkbox, a camera button (photo items fill up as photos
 * land), a note field — and the bottom section is the SRS return pickup email.
 *
 * All writes go through lib/pmBoard.ts. Photo items complete automatically at
 * min_photos; the return_email item completes when a pickup email is logged.
 *
 * Oct 1, 2026 — CompanyCam: crews shoot in the CompanyCam app. The sheet links
 * to the job's CompanyCam project, a photo item also completes when a
 * CompanyCam photo is TAGGED with the item's name (tagsMatchLabel), and the
 * return pickup can attach CompanyCam photos. The in-app camera stays as a
 * backup.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { mapsHref } from "../../lib/mapsHref";
import RulesList from "./RulesList";
import DoNotPerformCard, { readDoNotPerform } from "../deal/DoNotPerformCard";
import { PhotoViewer } from "../companycam/DealPhotosTab";
import {
  dayKey, fetchDealPhotos, fetchDealProjects, fmtCaptured, photoMatchesLabel, useCompanyCamRealtime,
  type CcPhoto, type CcProject,
} from "../../lib/companycam";
import {
  RETURN_ITEMS, addDaysISO, deleteJobPhoto, fmtDay, fmtStamp, itemDone, jobProgress, photoCountFor,
  sendReturnPickup, setTaskDone, setTaskNote, signedPhotoUrl, todayISO, uploadJobPhoto,
  type PmJob, type PmJobPhoto, type PmJobTask, type PmReturnSend, type PmRule, type PmTemplate, type ReturnLine,
} from "../../lib/pmBoard";

interface Props {
  job: PmJob;
  templates: PmTemplate[];
  rules: PmRule[];
  tasks: PmJobTask[];
  photos: PmJobPhoto[];
  lastReturn: PmReturnSend | null;
  names: Record<string, string>;
  memberId: string | null;
  onBack: () => void;
  onChanged: () => void;
}

// pm_workbook.post_build.<key>_return → the SRS line label it represents.
const RETURN_KEY_TO_ITEM: Record<string, string> = {
  duration_return: "Bundles OC Duration",
  proedge_return: "Bundles OC ProEdge",
  starter_return: "Bundles OC Starter",
  osb_return: "Sheets OSB",
  rhino_return: "Rolls Rhino Shield",
  ice_water_return: "Rolls Ice and Water",
};

export default function PmJobSheet(p: Props) {
  const { job, templates, rules, tasks, photos, lastReturn, names, memberId } = p;
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [ccPhotos, setCcPhotos] = useState<CcPhoto[]>([]);
  const [ccProjects, setCcProjects] = useState<CcProject[]>([]);
  const [viewing, setViewing] = useState<CcPhoto | null>(null);

  const loadCc = useCallback(async () => {
    try {
      const [proj, ph] = await Promise.all([fetchDealProjects(job.id), fetchDealPhotos([job.id])]);
      setCcProjects(proj); setCcPhotos(ph);
    } catch (e) { console.warn("CompanyCam read failed:", (e as Error).message); }
  }, [job.id]);
  useEffect(() => { void loadCc(); }, [loadCc]);
  useCompanyCamRealtime([job.id], loadCc);
  const ccProject = ccProjects[0] ?? null;
  const ccFor = (t: PmTemplate) => ccPhotos.filter((ph) => photoMatchesLabel(ph, t.label));
  const ccToday = ccPhotos.filter((ph) => dayKey(ph.captured_at) === todayISO()).length;

  const byTpl = useMemo(() => {
    const m: Record<string, PmJobTask> = {};
    for (const t of tasks) m[t.template_id] = t;
    return m;
  }, [tasks]);
  const returnSent = !!lastReturn && lastReturn.send_status === "sent";
  const prog = jobProgress(templates, tasks, photos, returnSent, ccPhotos.map((ph) => ph.tags));
  const pct = prog.total ? Math.round((prog.done / prog.total) * 100) : 0;

  const sections = useMemo(() => {
    const order: string[] = [];
    const map: Record<string, PmTemplate[]> = {};
    for (const t of templates) {
      if (!map[t.section]) { map[t.section] = []; order.push(t.section); }
      map[t.section].push(t);
    }
    return order.map((s) => ({ name: s, items: map[s] }));
  }, [templates]);

  const wb = (job.pm_workbook ?? {}) as Record<string, unknown>;
  const ev = (wb.eagleview ?? {}) as Record<string, unknown>;
  const sq = Number(ev.sq_with_waste) || Number(job.total_squares) || null;
  const instructions = Array.isArray(wb.special_instructions) ? (wb.special_instructions as string[]).filter(Boolean) : [];
  const punch = Array.isArray(wb.punch_list) ? (wb.punch_list as string[]).filter(Boolean) : [];
  const checklist = (wb.site_checklist ?? {}) as Record<string, string>;
  const checklistLines = Object.entries(checklist).filter(([, v]) => v && String(v).trim());
  const rm = job.owner_id ? names[job.owner_id] : null;
  const split = job.split_rm_id ? names[job.split_rm_id] : null;
  const addressLinkable = !!job.property_street && !!job.property_city;

  const toggle = useCallback(async (t: PmTemplate, next: boolean) => {
    setBusy(t.id); setErr(null);
    try { await setTaskDone(job.id, t.id, next, memberId); p.onChanged(); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  }, [job.id, memberId, p]);

  const saveNote = useCallback(async (t: PmTemplate, note: string) => {
    try { await setTaskNote(job.id, t.id, note); p.onChanged(); }
    catch (e) { setErr((e as Error).message); }
  }, [job.id, p]);

  const addPhotos = useCallback(async (t: PmTemplate | null, files: FileList | null) => {
    if (!files || files.length === 0) return;
    const key = t?.id ?? "__return";
    setBusy(key); setErr(null);
    try {
      for (const f of Array.from(files)) {
        await uploadJobPhoto({ dealId: job.id, templateId: t?.id ?? null, templateLabel: t?.label ?? "return stack", file: f, memberId });
      }
      p.onChanged();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  }, [job.id, memberId, p]);

  const removePhoto = useCallback(async (ph: PmJobPhoto) => {
    if (!window.confirm("Delete this photo?")) return;
    try { await deleteJobPhoto(ph); p.onChanged(); }
    catch (e) { setErr((e as Error).message); }
  }, [p]);

  return (
    <div>
      {/* Top bar */}
      <div className="mb-3 flex items-center gap-2">
        <button onClick={p.onBack} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 active:bg-slate-50">‹ Board</button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-lg font-extrabold text-slate-900">{job.homeowner_name || "(no name)"}</div>
          <div className="truncate text-xs text-slate-500">
            {job.build_scheduled_date ? `Build ${fmtDay(job.build_scheduled_date)}` : "No build date"}{job.build_completed_date ? " · Built ✓" : ""}
          </div>
        </div>
        <div className="text-right">
          <div className={`text-xl font-extrabold ${pct === 100 ? "text-emerald-600" : "text-slate-900"}`}>{prog.done}<span className="text-sm text-slate-400">/{prog.total}</span></div>
        </div>
      </div>
      <div className="mb-3 h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full transition-all ${pct === 100 ? "bg-emerald-500" : "bg-orange-500"}`} style={{ width: `${pct}%` }} />
      </div>

      {err && <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</div>}

      {/* Job header card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        {addressLinkable ? (
          <a href={mapsHref(job)} target="_blank" rel="noreferrer" className="block text-base font-semibold text-sky-700 underline-offset-2 active:underline">
            📍 {job.property_street}, {job.property_city}{job.property_state ? `, ${job.property_state}` : ""} {job.property_zip ?? ""}
          </a>
        ) : (
          <div className="text-base font-semibold text-slate-800">{job.property_street ?? "(no address)"}</div>
        )}
        <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
          {rm && <Chip>RM {rm}</Chip>}
          {split && <Chip>Split {split}</Chip>}
          {sq && <Chip>{sq} sq</Chip>}
          {job.shingle_color && <Chip>Shingle {job.shingle_color}</Chip>}
          {job.drip_edge_color && <Chip>Drip {job.drip_edge_color}</Chip>}
          {typeof wb.paint_color === "string" && wb.paint_color && <Chip>Paint {wb.paint_color}</Chip>}
          {typeof wb.drop_date === "string" && wb.drop_date && <Chip>Drop {fmtDay(wb.drop_date)}{typeof wb.drop_notes === "string" && wb.drop_notes ? ` · ${wb.drop_notes}` : ""}</Chip>}
          {job.claim_number && <Chip>Claim {job.claim_number}</Chip>}
        </div>
        {job.homeowner_phone && (
          <a href={`tel:${job.homeowner_phone.replace(/[^\d+]/g, "")}`} className="mt-2 inline-block rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-800">📞 Homeowner {job.homeowner_phone}</a>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {ccProject ? (
            <>
              <a href={ccProject.project_url ?? `https://app.companycam.com/projects/${ccProject.id}`} target="_blank" rel="noreferrer"
                className="rounded-lg bg-sky-600 px-3 py-1.5 text-sm font-bold text-white active:bg-sky-700">📸 Open in CompanyCam</a>
              <span className="text-xs text-slate-500">{ccPhotos.length} photo{ccPhotos.length === 1 ? "" : "s"}{ccToday ? ` · ${ccToday} today` : ""}</span>
            </>
          ) : (
            <span className="text-xs text-amber-700">No CompanyCam project linked to this job — ask Robert to link it on the deal's Photos tab.</span>
          )}
        </div>
        {(instructions.length > 0 || job.build_notes) && (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
            <div className="text-[11px] font-bold uppercase tracking-wide text-amber-800">Crew instructions</div>
            {instructions.length > 0 && (
              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-sm text-amber-900">{instructions.map((s, i) => <li key={i}>{s}</li>)}</ul>
            )}
            {job.build_notes && <div className="mt-1 whitespace-pre-wrap text-sm text-amber-900">{job.build_notes}</div>}
          </div>
        )}
        {/* Oct 30, 2026 — the carrier lines the homeowner kept: don't do them. */}
        <DoNotPerformCard items={readDoNotPerform(job.do_not_perform)} rmName={rm} className="mt-3" />
        {(checklistLines.length > 0 || punch.length > 0) && (
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {checklistLines.length > 0 && (
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Site checklist</div>
                <ul className="mt-1 space-y-0.5 text-sm text-slate-800">
                  {checklistLines.map(([k, v]) => <li key={k}><span className="capitalize text-slate-500">{k.replace(/_/g, " ")}:</span> {v}</li>)}
                </ul>
              </div>
            )}
            {punch.length > 0 && (
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Punch list on file</div>
                <ul className="mt-1 list-disc space-y-0.5 pl-4 text-sm text-slate-800">{punch.map((s, i) => <li key={i}>{s}</li>)}</ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Do / Don't */}
      {rules.length > 0 && (
        <div className="mt-3 rounded-2xl border border-slate-200 bg-white">
          <button onClick={() => setRulesOpen((o) => !o)} className="flex w-full items-center justify-between px-4 py-3 text-left">
            <span className="text-sm font-bold text-slate-800">✔ Do / ✖ Don't</span>
            <span className="text-xs text-slate-500">{rulesOpen ? "hide" : `${rules.length} rules`}</span>
          </button>
          {rulesOpen && <div className="border-t border-slate-100 px-4 py-3"><RulesList rules={rules} /></div>}
        </div>
      )}

      {/* Guide */}
      {sections.map((sec) => {
        const secDone = sec.items.filter((t) => t.required).filter((t) => itemDone(t, byTpl[t.id], photoCountFor(photos, t.id) + ccFor(t).length, returnSent)).length;
        const secTotal = sec.items.filter((t) => t.required).length;
        return (
          <div key={sec.name} className="mt-4">
            <div className="mb-1.5 flex items-baseline justify-between px-1">
              <h3 className="text-sm font-extrabold uppercase tracking-wide text-slate-700">{sec.name}</h3>
              <span className={`text-xs font-semibold ${secDone === secTotal && secTotal > 0 ? "text-emerald-600" : "text-slate-400"}`}>{secDone}/{secTotal}</span>
            </div>
            <div className="space-y-2">
              {sec.items.map((t) => (
                <GuideItem key={t.id} t={t} task={byTpl[t.id]} photos={photos.filter((ph) => ph.template_id === t.id)}
                  ccPhotos={t.kind === "photo" ? ccFor(t) : []} onOpenCc={setViewing}
                  returnSent={returnSent} busy={busy === t.id}
                  onToggle={(next) => toggle(t, next)} onNote={(n) => saveNote(t, n)}
                  onPhotos={(files) => addPhotos(t, files)} onRemovePhoto={removePhoto} />
              ))}
            </div>
          </div>
        );
      })}

      {/* Return pickup */}
      <ReturnPickup job={job} photos={photos} ccPhotos={ccPhotos} lastReturn={lastReturn} busy={busy === "__return"}
        onPhotos={(files) => addPhotos(null, files)} onRemovePhoto={removePhoto} onSent={p.onChanged} />

      {viewing && <PhotoViewer photo={viewing} deal={job} onClose={() => setViewing(null)} />}

      <div className="h-8" />
    </div>
  );
}

/* ------------------------------------------------------------------ */

function Chip({ children }: { children: React.ReactNode }) {
  return <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">{children}</span>;
}

function GuideItem({ t, task, photos, ccPhotos, onOpenCc, returnSent, busy, onToggle, onNote, onPhotos, onRemovePhoto }: {
  t: PmTemplate; task: PmJobTask | undefined; photos: PmJobPhoto[]; ccPhotos: CcPhoto[]; onOpenCc: (ph: CcPhoto) => void; returnSent: boolean; busy: boolean;
  onToggle: (next: boolean) => void; onNote: (note: string) => void; onPhotos: (files: FileList | null) => void; onRemovePhoto: (ph: PmJobPhoto) => void;
}) {
  const done = itemDone(t, task, photos.length + ccPhotos.length, returnSent);
  const total = photos.length + ccPhotos.length;
  const [note, setNote] = useState(task?.note ?? "");
  useEffect(() => { setNote(task?.note ?? ""); }, [task?.note]);
  const camRef = useRef<HTMLInputElement>(null);
  const libRef = useRef<HTMLInputElement>(null);

  return (
    <div className={`rounded-2xl border p-3 shadow-sm ${done ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white"}`}>
      <div className="flex items-start gap-3">
        {t.kind === "return_email" ? (
          <div className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm ${done ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400"}`}>{done ? "✓" : "✉"}</div>
        ) : (
          <button onClick={() => onToggle(!done)} disabled={busy} aria-pressed={done}
            className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold transition
              ${done ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-300 bg-white text-transparent active:border-orange-400"}`}>✓</button>
        )}
        <div className="min-w-0 flex-1">
          <div className={`text-sm font-semibold ${done ? "text-emerald-900" : "text-slate-900"}`}>
            {t.label}
            {!t.required && <span className="ml-1.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">optional</span>}
          </div>
          {t.hint && <div className="mt-0.5 text-xs text-slate-500">{t.hint}</div>}

          {t.kind === "photo" && (
            <div className="mt-2">
              <div className="text-[11px] text-slate-500">
                Shoot it in <strong>CompanyCam</strong> and tag it <strong>“{t.label}”</strong> — it checks off here on its own.
              </div>
              {ccPhotos.length > 0 && (
                <div className="mt-1.5 flex gap-2 overflow-x-auto pb-1">
                  {ccPhotos.map((ph) => (
                    <button key={ph.id} onClick={() => onOpenCc(ph)} title={fmtCaptured(ph.captured_at)}
                      className="block h-20 w-20 shrink-0 overflow-hidden rounded-xl border-2 border-sky-300 bg-slate-100">
                      {ph.uri_thumb && <img src={ph.uri_thumb} alt="" loading="lazy" className="h-full w-full object-cover" />}
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <button onClick={() => camRef.current?.click()} disabled={busy}
                  className="rounded-xl border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 active:bg-slate-50 disabled:opacity-60">
                  {busy ? "Uploading…" : "📷 Add here instead"}
                </button>
                <button onClick={() => libRef.current?.click()} disabled={busy}
                  className="rounded-xl border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 active:bg-slate-50 disabled:opacity-60">
                  🖼 Library
                </button>
                <span className={`text-xs font-semibold ${total > 0 ? "text-emerald-700" : "text-slate-500"}`}>{total === 0 ? "No photos yet" : `${total} photo${total === 1 ? "" : "s"}`}</span>
                <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { onPhotos(e.target.files); e.target.value = ""; }} />
                <input ref={libRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { onPhotos(e.target.files); e.target.value = ""; }} />
              </div>
              {photos.length > 0 && <PhotoStrip photos={photos} onRemove={onRemovePhoto} />}
            </div>
          )}

          {t.kind === "note" && (
            <textarea value={note} onChange={(e) => setNote(e.target.value)} onBlur={() => { if (note !== (task?.note ?? "")) onNote(note); }}
              rows={2} placeholder="Type it here…"
              className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none" />
          )}

          {t.kind === "return_email" && !done && (
            <div className="mt-1 text-xs text-slate-500">Complete this from the <strong>Return pickup</strong> section at the bottom.</div>
          )}
          {task?.done_at && t.kind !== "photo" && (
            <div className="mt-1 text-[11px] text-slate-400">Done {fmtStamp(task.done_at)}</div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Thumbnails with signed URLs (1h). Tap = open full size in a new tab. */
export function PhotoStrip({ photos, onRemove, selectable, selected, onSelect }: {
  photos: PmJobPhoto[]; onRemove?: (ph: PmJobPhoto) => void;
  selectable?: boolean; selected?: Set<string>; onSelect?: (ph: PmJobPhoto) => void;
}) {
  const [urls, setUrls] = useState<Record<string, string>>({});
  useEffect(() => {
    let cancelled = false;
    const missing = photos.filter((ph) => !urls[ph.id]);
    if (missing.length === 0) return;
    Promise.all(missing.map(async (ph) => [ph.id, await signedPhotoUrl(ph.storage_path)] as const)).then((pairs) => {
      if (cancelled) return;
      setUrls((prev) => { const n = { ...prev }; for (const [id, u] of pairs) if (u) n[id] = u; return n; });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos]);

  return (
    <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
      {photos.map((ph) => {
        const u = urls[ph.id];
        const isSel = selected?.has(ph.id);
        return (
          <div key={ph.id} className="relative shrink-0">
            {selectable ? (
              <button onClick={() => onSelect?.(ph)} className={`block h-20 w-20 overflow-hidden rounded-xl border-2 ${isSel ? "border-orange-500" : "border-slate-200"}`}>
                {u ? <img src={u} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full animate-pulse bg-slate-100" />}
                {isSel && <span className="absolute left-1 top-1 rounded-full bg-orange-500 px-1.5 text-[10px] font-bold text-white">✓</span>}
              </button>
            ) : (
              <a href={u || "#"} target="_blank" rel="noreferrer" className="block h-20 w-20 overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
                {u ? <img src={u} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full animate-pulse bg-slate-100" />}
              </a>
            )}
            {onRemove && (
              <button onClick={() => onRemove(ph)} aria-label="Delete photo"
                className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-slate-900/80 text-xs text-white">✕</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Return pickup                                                       */
/* ------------------------------------------------------------------ */

function ReturnPickup({ job, photos, ccPhotos, lastReturn, busy, onPhotos, onRemovePhoto, onSent }: {
  job: PmJob; photos: PmJobPhoto[]; ccPhotos: CcPhoto[]; lastReturn: PmReturnSend | null; busy: boolean;
  onPhotos: (files: FileList | null) => void; onRemovePhoto: (ph: PmJobPhoto) => void; onSent: () => void;
}) {
  const wb = (job.pm_workbook ?? {}) as Record<string, unknown>;
  const postBuild = (wb.post_build ?? {}) as Record<string, unknown>;

  // Pre-fill from the PM sheet's post-build return counts when present.
  const prefill = useMemo<ReturnLine[]>(() => {
    const out: ReturnLine[] = [];
    for (const [k, item] of Object.entries(RETURN_KEY_TO_ITEM)) {
      const q = Number(postBuild[k]);
      if (q > 0) out.push({ item, qty: q });
    }
    return out;
  }, [postBuild]);

  const [lines, setLines] = useState<ReturnLine[]>(() => (prefill.length ? prefill : [{ item: RETURN_ITEMS[0], qty: 1 }]));
  const [pickupDate, setPickupDate] = useState<string>(addDaysISO(todayISO(), 1));
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<boolean>(!lastReturn || lastReturn.send_status !== "sent");
  const camRef = useRef<HTMLInputElement>(null);
  const libRef = useRef<HTMLInputElement>(null);

  // Photos to attach: default = the ones taken under a "return" guide item, else the last photo.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const returnPhotos = photos; // any job photo can be attached
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size) return prev;
      const auto = photos.filter((ph) => (ph.file_name ?? "").includes("return")).map((ph) => ph.id);
      return new Set(auto);
    });
  }, [photos]);

  // CompanyCam photos offered for the email: today's, plus anything tagged
  // "return". Return-tagged ones from today are pre-selected.
  const ccChoices = useMemo(() => ccPhotos.filter((ph) =>
    dayKey(ph.captured_at) === todayISO() || ph.tags.some((t) => /return/i.test(t))).slice(0, 40), [ccPhotos]);
  const [ccSelected, setCcSelected] = useState<Set<string>>(new Set());
  const ccTouched = useRef(false);
  useEffect(() => {
    if (ccTouched.current) return;
    setCcSelected(new Set(ccChoices.filter((ph) => dayKey(ph.captured_at) === todayISO() && ph.tags.some((t) => /return/i.test(t))).map((ph) => ph.id)));
  }, [ccChoices]);
  const photoTotal = selected.size + ccSelected.size;

  const setLine = (i: number, patch: Partial<ReturnLine>) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const validLines = lines.filter((l) => l.item.trim() && l.qty > 0);

  const send = async () => {
    setErr(null);
    if (validLines.length === 0) { setErr("Add at least one item going back."); return; }
    if (photoTotal === 0 && !window.confirm("No photo attached. Send the pickup request without one?")) return;
    if (photoTotal > 6) { setErr("Pick 6 photos or fewer — that's the most one email carries."); return; }
    setSending(true);
    try {
      const paths = photos.filter((ph) => selected.has(ph.id)).map((ph) => ph.storage_path);
      await sendReturnPickup({ dealId: job.id, pickupDate, notes, lines: validLines, photoPaths: paths, companycamPhotoIds: [...ccSelected] });
      setOpen(false);
      onSent();
    } catch (e) { setErr((e as Error).message); }
    finally { setSending(false); }
  };

  return (
    <div className="mt-5 rounded-2xl border-2 border-orange-200 bg-white p-3 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-extrabold uppercase tracking-wide text-orange-700">🚚 Return pickup · SRS</div>
          <div className="text-xs text-slate-500">End of day: what's going back + a photo of the stack. Emails SRS Marietta, copies Robert and you, and fills in the Post-Build Tracking returns on Robert's PM sheet.</div>
        </div>
        {lastReturn && lastReturn.send_status === "sent" && (
          <div className="shrink-0 rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-bold text-emerald-800">Sent {fmtStamp(lastReturn.sent_at)}</div>
        )}
      </div>

      {lastReturn && lastReturn.send_status === "sent" && !open && (
        <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
          <div className="font-semibold">✓ Pickup requested for {lastReturn.pickup_date ? fmtDay(lastReturn.pickup_date) : "—"}</div>
          <ul className="mt-1 list-disc pl-4">{lastReturn.lines.map((l, i) => <li key={i}>{l.qty} × {l.item}</li>)}</ul>
          <div className="mt-1 text-xs text-emerald-700">{lastReturn.photo_paths.length} photo{lastReturn.photo_paths.length === 1 ? "" : "s"} attached.</div>
          <button onClick={() => setOpen(true)} className="mt-2 text-xs font-semibold text-emerald-800 underline">Send a corrected request</button>
        </div>
      )}
      {lastReturn && lastReturn.send_status === "failed" && (
        <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          ⚠ The last attempt ({fmtStamp(lastReturn.sent_at)}) didn't go out{lastReturn.error_message ? `: ${lastReturn.error_message}` : ""}. Send it again below.
        </div>
      )}

      {open && (
        <div className="mt-3 space-y-3">
          <div>
            <div className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">Going back</div>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input type="number" inputMode="numeric" min={0} value={l.qty || ""} onChange={(e) => setLine(i, { qty: Number(e.target.value) || 0 })}
                    className="w-16 rounded-xl border border-slate-300 px-2 py-2 text-center text-sm font-bold" />
                  <select value={RETURN_ITEMS.includes(l.item) ? l.item : "__other"} onChange={(e) => setLine(i, { item: e.target.value === "__other" ? "" : e.target.value })}
                    className="min-w-0 flex-1 rounded-xl border border-slate-300 bg-white px-2 py-2 text-sm">
                    {RETURN_ITEMS.map((it) => <option key={it} value={it}>{it}</option>)}
                    <option value="__other">Other…</option>
                  </select>
                  <button onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} aria-label="Remove line" className="rounded-lg px-2 py-2 text-slate-400 active:bg-slate-100">✕</button>
                  {!RETURN_ITEMS.includes(l.item) && (
                    <input value={l.item} onChange={(e) => setLine(i, { item: e.target.value })} placeholder="Item name"
                      className="basis-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                  )}
                </div>
              ))}
            </div>
            <button onClick={() => setLines((ls) => [...ls, { item: RETURN_ITEMS[0], qty: 1 }])} className="mt-2 text-sm font-semibold text-orange-600">+ Add a line</button>
          </div>

          <div>
            <div className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">Photo of the return stack</div>
            {ccChoices.length > 0 ? (
              <div className="mb-2">
                <div className="text-[11px] text-slate-500">From CompanyCam (today's photos + anything tagged “return”) — tap to attach:</div>
                <div className="mt-1 flex gap-2 overflow-x-auto pb-1">
                  {ccChoices.map((ph) => {
                    const on = ccSelected.has(ph.id);
                    return (
                      <button key={ph.id} title={fmtCaptured(ph.captured_at)}
                        onClick={() => { ccTouched.current = true; setCcSelected((s) => { const n = new Set(s); if (n.has(ph.id)) n.delete(ph.id); else n.add(ph.id); return n; }); }}
                        className={`relative block h-20 w-20 shrink-0 overflow-hidden rounded-xl border-2 ${on ? "border-orange-500" : "border-slate-200"}`}>
                        {ph.uri_thumb && <img src={ph.uri_thumb} alt="" loading="lazy" className="h-full w-full object-cover" />}
                        {on && <span className="absolute left-1 top-1 rounded-full bg-orange-500 px-1.5 text-[10px] font-bold text-white">✓</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="mb-2 text-[11px] text-slate-500">Snap the stack in CompanyCam — it shows up here to attach. Or add one below.</div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => camRef.current?.click()} disabled={busy}
                className="rounded-xl bg-orange-500 px-3 py-2 text-sm font-bold text-white shadow-sm active:bg-orange-600 disabled:opacity-60">{busy ? "Uploading…" : "📷 Take photo"}</button>
              <button onClick={() => libRef.current?.click()} disabled={busy}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 disabled:opacity-60">🖼 Library</button>
              <span className="text-xs text-slate-500">{photoTotal} selected to attach</span>
              <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { onPhotos(e.target.files); e.target.value = ""; }} />
              <input ref={libRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { onPhotos(e.target.files); e.target.value = ""; }} />
            </div>
            {returnPhotos.length > 0 ? (
              <>
                <div className="mt-1 text-[11px] text-slate-400">Tap photos to choose which go in the email (any photo from this job).</div>
                <PhotoStrip photos={returnPhotos} selectable selected={selected}
                  onSelect={(ph) => setSelected((s) => { const n = new Set(s); if (n.has(ph.id)) n.delete(ph.id); else n.add(ph.id); return n; })}
                  onRemove={onRemovePhoto} />
              </>
            ) : (
              <div className="mt-1 text-xs text-slate-400">No photos on this job yet.</div>
            )}
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">Pickup date</span>
              <input type="date" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">Notes for SRS</span>
              <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Stacked by the garage, gate code 1234…" className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />
            </label>
          </div>

          {err && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</div>}

          <button onClick={send} disabled={sending || busy}
            className="w-full rounded-xl bg-slate-900 px-4 py-3 text-base font-bold text-white shadow active:bg-slate-800 disabled:opacity-60">
            {sending ? "Sending…" : `Send return pickup to SRS (${validLines.length} line${validLines.length === 1 ? "" : "s"}, ${photoTotal} photo${photoTotal === 1 ? "" : "s"})`}
          </button>
        </div>
      )}
    </div>
  );
}
